import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Grid,
  Typography,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import {
  Equalizer,
  PanTool,
  TurnedIn,
  GroupAdd,
  AttachMoney,
  EmojiEvents,
  Comment as CommentIcon,
  ExpandMore,
  CheckCircle,
  RadioButtonUnchecked,
  TrendingUp,
} from "@material-ui/icons";

import SectionCard from "./SectionCard";
import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";
import {
  COUNCIL_ROLES,
  groupMembersByRole,
} from "../../../constants/councilRoles";
import {
  MAIN_PRIZE_OPTIONS,
  DURATION_TWO_YEAR,
  COUNCIL_TYPE_FINAL,
  SCHOOL_LEVEL_RESULT_OPTIONS,
  NATIONAL_LEVEL_RESULT_OPTIONS,
} from "../../../constants/projectStatus";

// StudentResearchHistory — render read-only TIMELINE toàn lifecycle đề tài SV.
// Dùng trong RegistrationForm `mode="view"` để user xem được mọi data đã nộp
// qua các giai đoạn (BCTĐ, đơn dừng, tổng kết, hội đồng, điểm, giải, nhận xét).
//
// Mỗi section conditional: chỉ render nếu phase đó có data — đề tài đang ở
// giai đoạn nào thì section trên/trước sẽ hiện, dưới/sau vắng tự nhiên.
//
// Read-only hoàn toàn: dropzone disabled, input disabled. UX preview lịch sử,
// không edit từ đây.
export default function StudentResearchHistory({ detail }) {
  if (!detail) return null;

  // BCTĐ tách bảng con — đọc detail.progressReports[] thay vì 4 cột progress_*
  // trên parent. Refactor 2026-08-26: chỉ còn 1 BCTĐ duy nhất.
  const progressReports = detail.progressReports || [];
  const hasReport = progressReports.length > 0;

  const hasStopRequest = !!detail.leaveReason;
  const hasFinalReport = !!detail.finalFileLink;

  const finalCouncil = (detail.councilAssignments || []).find(
    (a) => a.councilType === COUNCIL_TYPE_FINAL
  );
  // Hội đồng duyệt tiến độ đã bỏ (2026-08-26) — chỉ còn hiển thị cho dữ liệu
  // cũ đã gán trước đó, không tạo mới được nữa.
  const progressCouncil = (detail.councilAssignments || []).find(
    (a) => a.councilType === "progress"
  );
  const hasFinalCouncil = !!finalCouncil;
  const hasProgressCouncil = !!progressCouncil;

  const hasScore = (detail.point && detail.point > 0) || !!detail.estimateFileLink;
  const hasPrize =
    (detail.mainPrize && detail.mainPrize !== "") ||
    (detail.sidePrizes && detail.sidePrizes.length > 0);

  // 4 cấp chấm của đề tài SV. `proposed` là cờ đề xuất LÊN CẤP TIẾP THEO, do
  // người chấm cấp hiện tại tick; cấp Quốc tế là cấp cuối nên không có.
  const labelOf = (opts, v) => (v ? opts.find((o) => o.value === v)?.label || v : "");
  const levelRows = [
    {
      key: "department",
      level: "Cấp Khoa",
      prizeLabel: labelOf(MAIN_PRIZE_OPTIONS, detail.mainPrize),
      nextLabel: "Đề xuất lên cấp Trường",
      proposed: !!detail.eligible,
    },
    {
      key: "school",
      level: "Cấp Trường",
      prizeLabel: labelOf(SCHOOL_LEVEL_RESULT_OPTIONS, detail.schoolLevelPrize),
      nextLabel: "Đề xuất lên cấp Bộ / Quốc gia",
      proposed: !!detail.schoolLevelEligible,
    },
    {
      key: "national",
      level: "Cấp Quốc gia",
      prizeLabel: labelOf(NATIONAL_LEVEL_RESULT_OPTIONS, detail.nationalLevelPrize),
      nextLabel: "Đề xuất lên cấp Quốc tế",
      proposed: !!detail.nationalLevelEligible,
    },
    {
      key: "international",
      level: "Cấp Quốc tế",
      prizeLabel: labelOf(SCHOOL_LEVEL_RESULT_OPTIONS, detail.internationalLevelPrize),
      nextLabel: "", // cấp cuối
      proposed: false,
    },
  ];
  // Chỉ hiện khi đề tài đã qua chấm cấp Khoa — trước đó chưa có gì để nói.
  const hasLevelResult =
    !!detail.mainPrize ||
    !!detail.schoolLevelPrize ||
    !!detail.nationalLevelPrize ||
    !!detail.internationalLevelPrize;

  const hasAnyComment =
    detail.registrationComment ||
    detail.conductComment ||
    detail.progressComment ||
    detail.acceptanceComment;

  // Helper render danh sách file từ "url1||url2" → MultiFileManager read-only.
  const renderFileList = (raw) => {
    const urls = raw ? String(raw).split("||").filter(Boolean) : [];
    return (
      <MultiFileManager
        fileUrls={urls}
        setFileUrls={() => {}}
        newFiles={[]}
        setNewFiles={() => {}}
        disabled={true}
      />
    );
  };

  return (
    <>
      {/* ─── BÁO CÁO TIẾN ĐỘ (1 lần, refactor 2026-08-26) ─── */}
      {hasReport && progressReports
        .slice()
        .sort((a, b) => (a.iteration || 0) - (b.iteration || 0))
        .map((rep) => (
          <SectionCard
            key={`progress-${rep.iteration}`}
            icon={<Equalizer />}
            title={`Báo cáo tiến độ — ${rep.status === "accepted" ? "Đạt" : rep.status === "submitted" ? "Chờ duyệt" : rep.status === "adjust_request" ? "Yêu cầu sửa" : rep.status === "rejected" ? "Không đạt" : "Nháp"}`}
          >
            {rep.submittedAt && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                Đã nộp: {dayjs(rep.submittedAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
            )}
            {rep.reviewComment && (
              <Box marginBottom={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
                <Typography variant="caption" color="textSecondary"><b>Nhận xét của Trưởng khoa:</b></Typography>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{rep.reviewComment}</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {[
                { label: "1. Công việc đã hoàn thành", value: rep.completedWork },
                { label: "2. Đánh giá tiến độ", value: rep.evaluation },
                { label: "3. Kế hoạch tiếp theo", value: rep.nextPlan },
                { label: "4. Kiến nghị", value: rep.recommendation },
              ].map((f, i) => (
                <Grid item xs={12} key={i}>
                  <Typography variant="body2" style={{ marginBottom: 6 }}>
                    <b>{f.label}</b>
                  </Typography>
                  <TextField
                    fullWidth multiline minRows={2} variant="outlined" disabled
                    value={f.value || "(chưa nhập)"}
                  />
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        ))}

      {/* ─── ĐƠN XIN DỪNG ─── */}
      {hasStopRequest && (
        <SectionCard icon={<PanTool />} title="Đơn xin dừng đề tài">
          <Typography variant="body2" style={{ marginBottom: 6 }}>
            <b>Lý do</b>
          </Typography>
          <TextField
            fullWidth multiline minRows={3} variant="outlined" disabled
            value={detail.leaveReason || ""}
            style={{ marginBottom: 16 }}
          />
          {detail.leaveFileLink && (
            <>
              <Typography variant="body2" style={{ marginBottom: 6 }}>
                <b>File đơn</b>
              </Typography>
              {renderFileList(detail.leaveFileLink)}
            </>
          )}
        </SectionCard>
      )}

      {/* ─── BÁO CÁO TỔNG KẾT ─── */}
      {hasFinalReport && (
        <SectionCard icon={<TurnedIn />} title="Báo cáo tổng kết">
          {renderFileList(detail.finalFileLink)}
        </SectionCard>
      )}

      {/* ─── HỘI ĐỒNG TIẾN ĐỘ (legacy, chỉ hiện với dữ liệu cũ) ─── */}
      {hasProgressCouncil && (
        <SectionCard icon={<GroupAdd />} title="Hội đồng duyệt báo cáo tiến độ (đã bỏ)">
          <CouncilMembersView assignment={progressCouncil} />
        </SectionCard>
      )}

      {/* ─── HỘI ĐỒNG NGHIỆM THU ─── */}
      {hasFinalCouncil && (
        <SectionCard icon={<GroupAdd />} title="Hội đồng nghiệm thu">
          <CouncilMembersView assignment={finalCouncil} />
        </SectionCard>
      )}

      {/* ─── CHẤM ĐIỂM NGHIỆM THU ─── */}
      {hasScore && (
        <SectionCard icon={<AttachMoney />} title="Chấm điểm nghiệm thu">
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="body2" style={{ marginBottom: 6 }}>
                <b>Điểm</b>
              </Typography>
              <TextField fullWidth variant="outlined" disabled value={detail.point ?? ""} />
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body2" style={{ marginBottom: 6 }}>
                <b>Phiếu đánh giá nghiệm thu</b>
              </Typography>
              {detail.estimateFileLink ? (
                renderFileList(detail.estimateFileLink)
              ) : (
                <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                  (chưa có phiếu đánh giá)
                </Typography>
              )}
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* ─── GIẢI THƯỞNG ─── */}
      {hasPrize && (
        <SectionCard icon={<EmojiEvents />} title="Giải thưởng">
          {detail.mainPrize && (
            <Box marginBottom={1.5}>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                <b>Giải chính</b>
              </Typography>
              <Chip
                label={MAIN_PRIZE_OPTIONS.find((o) => o.value === detail.mainPrize)?.label || detail.mainPrize}
                color="primary"
                size="small"
              />
            </Box>
          )}
          {detail.sidePrizes && detail.sidePrizes.length > 0 && (
            <Box>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                <b>Giải phụ ({detail.sidePrizes.length})</b>
              </Typography>
              <Box display="flex" flexDirection="column" gridGap={6}>
                {detail.sidePrizes.map((sp, i) => (
                  <Box key={i} padding="6px 12px" bgcolor="#fafafa" border="solid 1px #eee" borderRadius={4}>
                    <Typography variant="body2">
                      <b>{sp.name || "(chưa đặt tên)"}</b>
                      {sp.level && <span style={{ marginLeft: 8, color: "#666" }}>— {sp.level}</span>}
                    </Typography>
                    {sp.description && (
                      <Typography variant="caption" color="textSecondary">
                        {sp.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </SectionCard>
      )}

      {/* ─── KẾT QUẢ THEO CẤP + ĐỀ XUẤT LÊN CẤP TRÊN ───
          Thêm 2026-09-11: 3 cờ `eligible` / `schoolLevelEligible` /
          `nationalLevelEligible` vốn chỉ có ở form CHẤM ĐIỂM, không hiển thị ở
          đâu cả → xem đề tài xong không biết nó có được đề xuất lên cấp trên
          hay không. */}
      {hasLevelResult && (
        <SectionCard icon={<TrendingUp />} title="Kết quả theo cấp & đề xuất lên cấp trên">
          <Box display="flex" flexDirection="column" gridGap={8}>
            {levelRows.map((r) => (
              <Box
                key={r.key}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gridGap={8}
                padding="8px 12px"
                bgcolor="#fafafa"
                border="solid 1px #eee"
                borderRadius={4}
              >
                <Typography variant="body2" style={{ minWidth: 150 }}>
                  <b>{r.level}</b>
                </Typography>
                {r.prizeLabel ? (
                  <Chip label={r.prizeLabel} size="small" color="primary" />
                ) : (
                  <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
                    chưa có kết quả
                  </Typography>
                )}
                {r.nextLabel && (
                  <Box display="flex" alignItems="center" gridGap={4} marginLeft="auto">
                    {r.proposed ? (
                      <CheckCircle fontSize="small" style={{ color: "#4caf50" }} />
                    ) : (
                      <RadioButtonUnchecked fontSize="small" style={{ color: "#bdbdbd" }} />
                    )}
                    <Typography
                      variant="body2"
                      style={{ color: r.proposed ? "#2e7d32" : "#9e9e9e" }}
                    >
                      {r.nextLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}

      {/* ─── LỊCH SỬ NHẬN XÉT / DUYỆT ─── */}
      {hasAnyComment && (
        <SectionCard icon={<CommentIcon />} title="Lịch sử nhận xét">
          {[
            { key: "registrationComment", label: "Vòng duyệt đăng ký", value: detail.registrationComment },
            { key: "conductComment", label: "Vòng duyệt thực hiện (Phòng QLKH)", value: detail.conductComment },
            { key: "progressComment", label: "Vòng duyệt báo cáo tiến độ", value: detail.progressComment },
            { key: "acceptanceComment", label: "Vòng nghiệm thu cuối", value: detail.acceptanceComment },
          ]
            .filter((r) => !!r.value)
            .map((r) => (
              <Accordion key={r.key} elevation={0} style={{ border: "solid 1px #e0e0e0", marginBottom: 4 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2">
                    <b>{r.label}</b>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" style={{ whiteSpace: "pre-wrap", width: "100%" }}>
                    {r.value}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
        </SectionCard>
      )}
    </>
  );
}

// CouncilMembersView — render danh sách thành viên 1 assignment, grouped theo
// role (chairman/secretary/reviewer/commissioner). Read-only display.
function CouncilMembersView({ assignment }) {
  const grouped = groupMembersByRole(assignment?.members || []);
  return (
    <Box>
      {COUNCIL_ROLES.map((role) => {
        const list = grouped[role.key] || [];
        if (list.length === 0) return null;
        return (
          <Box key={role.key} marginBottom={1.5}>
            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <b>{role.label}</b>
              <span style={{ color: "#9e9e9e", marginLeft: 8, fontSize: 12 }}>
                ({list.length})
              </span>
            </Typography>
            <Box display="flex" flexDirection="column" gridGap={4}>
              {list.map((m, i) => (
                <Box
                  key={`${m.roleKey}-${m.orderNum}-${i}`}
                  padding="6px 12px"
                  bgcolor="#fafafa"
                  border="solid 1px #eee"
                  borderRadius={4}
                >
                  <Typography variant="body2">
                    {m.degree && <span style={{ color: "#666" }}>{m.degree} </span>}
                    <b>{m.name || "(chưa nhập tên)"}</b>
                    {!m.isPhenikaa && (
                      <Chip
                        size="small"
                        label="Ngoài trường"
                        style={{ marginLeft: 8, height: 18, fontSize: 11 }}
                      />
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
